/** Copyright © 2025 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/
import { AC_SYSTEM_FIELD_TYPE_CODE } from '@hems/util/src/constant/constant';
import { AC_DEVICE_TYPE, ACSystemModelNamePrefixes, EMS_TYPE } from '@hems/util/src/constant/device';
export const SETTINGS_SUB_TAB_NAME = {
    INSTALLATION: 'Installation',
    BASIC: 'BasicSettings',
    ENGINEER: 'EngineerSettings',
    ADVANCED: 'AdvancedSettings',
    ENERGY_MANAGEMENT: 'EnergyManagement',
};
export const SETTING_TYPE = {
    BASIC: 'BS',
    ADVANCED: 'AS',
    TOU: 'TOU',
    BASIC_ADVANCED: 'BS_AS',
    ADVANCED_TOU: 'AS_TOU',
    BASIC_TOU: 'BS_TOU',
    BASIC_ADVANCED_TOU: 'BS_AS_TOU',
    ENGINEER: 'ES',
};
export const SETTING_TARGET = {
    /** Site */
    SITE: 'site',
    /** AC Combiner */
    AC_COMBINER: 'accb',
    /** ACES 전체 */
    AC_ESS: 'aces',
    /** ACES 개별 */
    AC_EES_INDIVIDUAL: 'aces_individual',
    /** 모든 장비 개별 */
    INDIVIDUAL: 'individual',
};
const AC_DEVICE = {
    AC_COMBINER: 'AC Combiner',
    AC_COUPLED_ESS_HUB: 'AC Coupled ESS + HUB',
};
export const SETTING_TAB_CODE = {
    SITE: 'site',
    DEVICE: 'device',
};
let engineerDeviceOption = [];
let controlDeviceOption = [];
const combinerOption = { text: AC_DEVICE.AC_COMBINER, value: AC_SYSTEM_FIELD_TYPE_CODE.ACCB };
const ESSHubOption = {
    text: AC_DEVICE.AC_COUPLED_ESS_HUB,
    value: AC_SYSTEM_FIELD_TYPE_CODE.ACES,
};
const clearOptions = () => {
    engineerDeviceOption = [];
    controlDeviceOption = [];
};
const isACCoupledESS = (productModelName) => productModelName.startsWith(ACSystemModelNamePrefixes[1]);
const setIndividualOption = (deviceList) => {
    deviceList.forEach((device) => {
        if (isACCoupledESS(device.deviceInfo.product_model_nm)) {
            const individualOption = {
                text: `AC Coupled ESS (serial #${device.deviceInfo.device_id})`,
                value: `${AC_SYSTEM_FIELD_TYPE_CODE.ACES_INDIVIDUAL}_${device.deviceInfo.device_id}`,
            };
            engineerDeviceOption.push(individualOption);
            controlDeviceOption.push(individualOption);
        }
    });
};
const setCase1Option = () => {
    engineerDeviceOption.push(combinerOption);
    controlDeviceOption.push(combinerOption);
};
const setCase3Option = (deviceList) => {
    controlDeviceOption.push(combinerOption);
    engineerDeviceOption.push(combinerOption);
    engineerDeviceOption.push(ESSHubOption);
    setIndividualOption(deviceList);
};
const setCase4Option = (deviceList) => {
    engineerDeviceOption.push(ESSHubOption);
    setIndividualOption(deviceList);
};
const setCase5Option = (deviceList) => {
    setIndividualOption(deviceList);
};
const setCase6Option = (deviceList) => {
    controlDeviceOption.push(combinerOption);
    engineerDeviceOption.push(combinerOption);
    setIndividualOption(deviceList);
};
export const getDeviceTabListForAC = (acDeviceType, deviceList) => {
    const setDeviceTabList = {
        [AC_DEVICE_TYPE.AC_COMBINER]: () => setCase1Option(),
        [AC_DEVICE_TYPE.AC_COMBINER_MULTI_ESS_HUB]: () => setCase3Option(deviceList),
        [AC_DEVICE_TYPE.AC_THIRD_PARTY_MULTI_ESS_HUB]: () => setCase4Option(deviceList),
        [AC_DEVICE_TYPE.AC_THIRD_PARTY_ESS_HUB]: () => setCase4Option(deviceList),
        [AC_DEVICE_TYPE.AC_ESS]: null,
        [AC_DEVICE_TYPE.AC_COMBINER_MULTI_ESS]: () => setCase6Option(deviceList),
        [AC_DEVICE_TYPE.AC_MULTI_ESS]: null,
        [AC_DEVICE_TYPE.AC_THIRD_PARTY_ESS]: () => setCase5Option(deviceList),
    };
    const setDeviceTabListAction = setDeviceTabList[acDeviceType];
    clearOptions();
    setDeviceTabListAction?.();
    return { engineerDeviceOption, controlDeviceOption };
};
/** 3rd Party ESS
 * Setting 항목 : Compatible ESS - 1,2,3,4 (Case 9, R09 이상) */
export const THIRD_PARTY_ESS = {
    /** Disabled (그 외, Enabled) */
    DISABLED: 0,
    /** Case9, Qcells Gen-2 (Q.VOLT)  */
    QCELLS_GEN_2: 1,
    /** Case9, Tesla Powerwall 3  */
    TESLA_POWERWALL_3: 2,
    /** Case9, FranklinWH aPower 1.3 */
    FRANKLINWH_APOWER_1_3: 3,
    /** Case9, FranklinWH aPower 2.0 */
    FRANKLINWH_APOWER_2_0: 4,
    /** Other  */
    OTHERS: 99,
};
export const MPU_AVOIDANCE = {
    NONE: 0,
    BACKFEED_LIMIT: 1,
    BBOC: 2,
    /** Case9, Tesla Powerwall 3 (R09 이상) */
    BFPL_AND_BBOC: 3,
};
export const NEC_120_RULE_RATE = 1.2;
export const BACKUP_TYPE = {
    NONE: 0,
    WHOLE_HOME_BACKUP: 1,
    PARTIAL_HOME_BACKUP: 2,
};
export const GRID_SUPPORT_SUB_PANEL = {
    NOT_INSTALLED: 0,
    INSTALLED: 1,
};
export const CASE_SETTING_TARGET_MAP = {
    1: {
        [SETTING_TARGET.SITE]: [EMS_TYPE.STAND_ALONE],
        [SETTING_TARGET.AC_COMBINER]: [EMS_TYPE.STAND_ALONE],
        [SETTING_TARGET.AC_ESS]: [],
        [SETTING_TARGET.AC_EES_INDIVIDUAL]: [],
        [SETTING_TARGET.INDIVIDUAL]: [EMS_TYPE.STAND_ALONE],
    },
    3: {
        [SETTING_TARGET.SITE]: [EMS_TYPE.STAND_ALONE, EMS_TYPE.PRIMARY],
        [SETTING_TARGET.AC_COMBINER]: [EMS_TYPE.STAND_ALONE],
        [SETTING_TARGET.AC_ESS]: [EMS_TYPE.PRIMARY],
        [SETTING_TARGET.AC_EES_INDIVIDUAL]: [EMS_TYPE.PRIMARY, EMS_TYPE.SECONDARY],
        [SETTING_TARGET.INDIVIDUAL]: [EMS_TYPE.STAND_ALONE, EMS_TYPE.PRIMARY, EMS_TYPE.SECONDARY],
    },
    '4_MULTI_ESS': {
        [SETTING_TARGET.SITE]: [EMS_TYPE.PRIMARY],
        [SETTING_TARGET.AC_COMBINER]: [],
        [SETTING_TARGET.AC_ESS]: [EMS_TYPE.PRIMARY],
        [SETTING_TARGET.AC_EES_INDIVIDUAL]: [EMS_TYPE.PRIMARY, EMS_TYPE.SECONDARY],
        [SETTING_TARGET.INDIVIDUAL]: [EMS_TYPE.PRIMARY, EMS_TYPE.SECONDARY],
    },
    '4_SINGLE_ESS': {
        [SETTING_TARGET.SITE]: [EMS_TYPE.STAND_ALONE],
        [SETTING_TARGET.AC_COMBINER]: [],
        [SETTING_TARGET.AC_ESS]: [EMS_TYPE.STAND_ALONE],
        [SETTING_TARGET.AC_EES_INDIVIDUAL]: [EMS_TYPE.STAND_ALONE],
        [SETTING_TARGET.INDIVIDUAL]: [EMS_TYPE.STAND_ALONE],
    },
    5: {
        [SETTING_TARGET.SITE]: [EMS_TYPE.STAND_ALONE],
        [SETTING_TARGET.AC_COMBINER]: [],
        [SETTING_TARGET.AC_ESS]: [EMS_TYPE.STAND_ALONE],
        [SETTING_TARGET.AC_EES_INDIVIDUAL]: [EMS_TYPE.STAND_ALONE],
        [SETTING_TARGET.INDIVIDUAL]: [EMS_TYPE.STAND_ALONE],
    },
    6: {
        [SETTING_TARGET.SITE]: [EMS_TYPE.PRIMARY, EMS_TYPE.SECONDARY],
        [SETTING_TARGET.AC_COMBINER]: [EMS_TYPE.PRIMARY],
        [SETTING_TARGET.AC_ESS]: [EMS_TYPE.SECONDARY],
        [SETTING_TARGET.AC_EES_INDIVIDUAL]: [EMS_TYPE.SECONDARY],
        [SETTING_TARGET.INDIVIDUAL]: [EMS_TYPE.PRIMARY, EMS_TYPE.SECONDARY],
    },
    /** @TODO 추후 Case8 개발 시에 추가 필요 */
    8: {
        [SETTING_TARGET.SITE]: [],
        [SETTING_TARGET.AC_COMBINER]: [],
        [SETTING_TARGET.AC_ESS]: [],
        [SETTING_TARGET.AC_EES_INDIVIDUAL]: [],
        [SETTING_TARGET.INDIVIDUAL]: [],
    },
};
export const BATTERY_PERMISSION_OPTION = {
    UNRESTRICTED: 0,
    IMPORT_ONLY: 1,
    EXPORT_ONLY: 2,
    NO_EXCHANGE: 3,
};
/** MQTT Protocol > Configuration - EnergyPolicy 옵션 */
export const ENERGY_POLICY = {
    STAND_BY: 0,
    SELF_CONSUMPTION: 1,
    /** Limit Export */
    ZERO_EXPORT: 2,
    TIME_BASED: 3,
    EXTERNAL_GENERATION: 4,
    SMART_SAVING: 5,
};
export const INIT_GRID_CODE = -1;
export const AC_COMBINER_GRID_CODE = {
    /** 미국 UL 1741 SA */
    UL_1741_SA: 8451,
    /** 미국 IEEE 1547-2018 */
    IEEE_1547_2018: 8452,
    /** Rule 21 (California) */
    CA_RULE_21: 8453,
    /** 미국 HECO */
    HECO: 8454,
    /** CenHud (New York) */
    CENHUD: 8455,
    /** 미국 IEEE 1547-2018_Extend */
    IEEE_1547_2018_EXTEND: 8456,
    /** LUMA (Puerto Rico) */
    LUMA: 8457,
    /** Ameren (Illinois) */
    AMEREN: 8458,
    /** IEEE 1547-2015 */
    IEEE_1547_2015: 8459,
    /** PNM IEEE 1547-2018 */
    PNM_IEEE1547_2018: 8460,
    /** Minnesota */
    MINNESOTA: 8461,
    /** Dakota */
    DAKOTA: 8462,
    /** Alliant Energy (Iowa) */
    ALLIANT_ENERGY_IOWA: 8463,
    /** Orange Rockland (Rockland Electric) */
    ORANGE_ROCKLAND: 8464,
};
export const AC_COUPLED_GRID_CODE = {
    /** 미국 UL 1741 SA */
    UL_1741_SA: 8401,
    /** 미국 IEEE 1547-2018 */
    IEEE_1547_2018: 8402,
    /** Rule 21 (California) */
    CA_RULE_21: 8403,
    /** 미국 HECO */
    HECO: 8404,
    /** CenHud (New York) */
    CENHUD: 8406,
    /** 미국 IEEE 1547-2018_Extend */
    IEEE_1547_2018_EXTEND: 8407,
    /** LUMA (Puerto Rico) */
    LUMA: 8408,
    /** Ameren (Illinois) */
    AMEREN: 8409,
    /** IEEE 1547-2015 */
    IEEE_1547_2015: 8410,
    /** PNM IEEE 1547-2018 */
    PNM_IEEE1547_2018: 8411,
    /** Minnesota */
    MINNESOTA: 8412,
    /** Dakota */
    DAKOTA: 8413,
    /** Alliant Energy (Iowa) */
    ALLIANT_ENERGY_IOWA: 8414,
    /** Orange Rockland (Rockland Electric) */
    ORANGE_ROCKLAND: 8415,
};
/**
 * Normal Ramp Rate를 installer 화면/리포트에 표시하는 grid profile.
 * ACCB(microInverterEnaRmp/WRmp), ACES(gradientActivePowerSetPointGradient) 모두 같은 4개 utility다.
 */
export const NORMAL_RAMP_RATE_GRID_CODES = {
    ACCB: [
        AC_COMBINER_GRID_CODE.UL_1741_SA,
        AC_COMBINER_GRID_CODE.CA_RULE_21,
        AC_COMBINER_GRID_CODE.LUMA,
        AC_COMBINER_GRID_CODE.AMEREN,
    ],
    ACES: [
        AC_COUPLED_GRID_CODE.UL_1741_SA,
        AC_COUPLED_GRID_CODE.CA_RULE_21,
        AC_COUPLED_GRID_CODE.LUMA,
        AC_COUPLED_GRID_CODE.AMEREN,
    ],
};
export const INSTALLER_EXCEPTION_GRID_CODES = {
    // MI_Ameren_(Illinois): ACCB/ACES 모두 Under Voltage에 UV3 예외가 있다.
    ACCB_MI_AMEREN_ILLINOIS: AC_COMBINER_GRID_CODE.AMEREN,
    ACES_UL_AMEREN_ILLINOIS_240V: AC_COUPLED_GRID_CODE.AMEREN,
};
export const OVER_09_EMS_VERSION_GRID_CODE = {
    LUMA: 'LUMA',
    IEEE_1547_2018_EXTEND: 'IEEE1547-2018_Extend',
    // R090214 이상일 때만 노출.
    PNM_IEEE1547_2018: 'PNM IEEE 1547-2018',
};
export const SETTING_FLAG = {
    DISABLE: 0,
    ENABLE: 1,
};
